// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2023-03-23 15:22:51 +0100 (Thu, 23 Mar 2023) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 133215 $
//  Purpose   : viewer layouts
// =============================================================================
// ---Third-party imports
import RBush from 'rbush'

// ---Framework imports
import {includes, dict, merge, range} from 'ldash'
import log from 'uix/logging'
import {hashObj} from 'uix/utils'

// ---Globals

//~ let SIN = new Map()
//~ let COS = new Map()
//~ for (let d of range(360)) {
//~ SIN.set(d, Math.sin((d * Math.PI) / 180))
//~ COS.set((d + 90) % 360, Math.cos((((d + 90) % 360) * Math.PI) / 180))
//~ }

// layoutId generator
let LAYOUT_ID = 0
export let layoutId = () => {
  LAYOUT_ID += 1
  return LAYOUT_ID
}

let index = (areas, spatialIndex) => {
  // general precalculations
  let bushData = []
  let width = 0,
    height = 0
  let byId = {}

  for (let e of areas) {
    byId[e.id] = e

    // build spatial index (rtree variant)
    if (spatialIndex) {
      bushData.push({
        id: e.id,
        minX: e.x,
        minY: e.y,
        maxX: e.x + e.width,
        maxY: e.y + e.height,
      })
    }

    let w = e.x + e.width,
      h = e.y + e.height
    if (w > width) width = w
    if (h > height) height = h
  }

  // maybe use kdbush - is 5-8 times faster during indexing
  // but not mutable (so what, this is react)
  let bush = new RBush()
  bush.load(bushData)
  let center = {x: width / 2, y: height / 2}
  return {byId, bush, width, height, center, count: areas.length}
}

let squareLayout = (numAreas, gutter_x, gutter_y) => {
  let next_y = 0
  let rowCount = parseInt(Math.sqrt(numAreas))

  let PAGE_WIDTH = 1 // assumed constant here

  return (i, a) => {
    let {width, height} = a
    let colNum = parseInt(i / rowCount)
    let y = i % rowCount

    if (y == 0) {
      next_y = 0
    } else {
      y = next_y
    }
    next_y += height + gutter_y

    return {
      left: colNum * (PAGE_WIDTH + (colNum > 0 ? gutter_x : 0)),
      top: y,
    }
  }
}

let horizontalMultiPage = (gutter_x, n, offset) => {
  let next_x = 0
  let m = (n - 1 + offset) % n
  return (i, a) => {
    let x = next_x
    next_x += a.width
    if (i % n == m) {
      next_x += gutter_x
    }
    return {left: x, top: 0}
  }
}

let verticalMultiPage = (gutter_y, n, offset) => {
  let next_x = 0
  let next_y = 0
  let heights = []
  let m = (n - 1 + offset) % n
  return (i, a) => {
    let y = next_y
    let x = next_x

    heights.push(a.height)
    next_x += a.width

    if (i % n == m) {
      next_y += Math.max(...heights)
      next_y += gutter_y

      heights.length = 0
      next_x = 0
    }
    return {left: x, top: y}
  }
}

let treeLayout = (gutter_y, gutter_x) => {
  let next_x = 0
  let next_y = 0
  let heights = []
  let pid = null
  return (i, a, p) => {
    let y = next_y
    let x = i > 0 ? next_x : p.level

    if (p.pid != pid) {
      y = heights.length > 0 ? next_y + Math.max(...heights) + gutter_y * 4.5 : 0
      next_y = y

      heights.length = 0
      x = p.level
      next_x = x
      pid = p.pid
    }

    heights.push(a.height)
    next_x += a.width + gutter_x / 2
    return {left: x, top: y}
  }
}

let I2CM = 2.54
let DINA4CM = 21 / I2CM

export let Layout = (pages, {...options}) => {
  let {align, gutter_x, gutter_y, limit, spatialIndex, dpiAware} = options
  let slicedPages = limit ? pages.slice(0, limit) : pages
  //~ log('creating layout for %o pages, align: %o, limit: %o', slicedPages.length, align, limit)

  // set pages to their positions
  let areas = []
  //~ log(align)
  let layoutAlgo = () => {
    switch (align) {
      case 'square':
        return squareLayout(slicedPages.length, gutter_x, gutter_y)
      case 'vertical':
        return verticalMultiPage(gutter_y, 1, 0)
      case 'horizontal':
        return horizontalMultiPage(gutter_x, 1, 0)
      case 'tree':
        return treeLayout(gutter_x, gutter_y)
      case 'horizontal.2':
        return horizontalMultiPage(gutter_x, 2, 0)
      case 'horizontal.2.1':
        return horizontalMultiPage(gutter_x, 2, 1)
      case 'horizontal.4':
        return horizontalMultiPage(gutter_x, 4, 0)
      case 'horizontal.4.1':
        return horizontalMultiPage(gutter_x, 4, 1)
      case 'vertical.2':
        return verticalMultiPage(gutter_y, 2, 0)
      case 'vertical.2.1':
        return verticalMultiPage(gutter_y, 2, 1)
      case 'vertical.4':
        return verticalMultiPage(gutter_y, 4, 0)
      case 'vertical.4.2':
        return verticalMultiPage(gutter_y, 4, 2)
      default:
        return horizontalMultiPage(gutter_x, 1, 0)
    }
  }
  let position = layoutAlgo()
  for (let [i, page] of slicedPages.entries()) {
    let {size, id, pbox} = page
    if (!size) {
      continue
    }

    let [w, h] = size
    if (pbox) {
      // see getPageBoxCSS and renderPage
      w = pbox.right - pbox.left
      h = pbox.bottom - pbox.top
    }
    let dinA4Relative = 1
    if (dpiAware) {
      // prevent Infinity: a dpi value of 0 needs to be modified to a positive integer
      let dpi = page.dpi || 72
      dinA4Relative = w / DINA4CM / dpi
    }

    let area = {
      width: dinA4Relative, //
      height: (h / w) * dinA4Relative, // normalized height, aka aspectRatio
      id,
      orgWidth: w,
      page, // by reference
      //~ rotation: 10,
    }

    //~ if (area.rotation) {
    //~ let sin = SIN.get(area.rotation)
    //~ let cos = COS.get(area.rotation)

    //~ let {height, width} = area

    //~ area.width = sin * height + cos * width
    //~ area.height = sin * width + cos * height

    //~ area.unrotatedWidth = width / area.width
    //~ area.unrotatedHeight = height / area.height

    //~ }
    //~ area.width = area.height
    //~ area.height = dinA4Relative

    let {width: awidth, height: aheight} = area
    let {left, top} = position(i, area, page)

    // setting the props {x (left), y (top), width, height} makes us
    // compatible with RenderPane._cellSizeAndPositionGetter
    area = {
      ...area,
      x: left,
      y: top,
      center: {
        x: left + awidth / 2,
        y: top + aheight / 2,
      },
    }
    areas.push(area)
  }

  let {byId, bush, width, height, center, count} = index(areas, spatialIndex)

  return {
    id: layoutId(),
    areas, // layouted areas
    pages, // original input
    // precalculated stuff
    byId, // id to area mapping
    width, // full layout width
    height, // full layout height
    width_g: width + options.gutter_x, // width + gutter
    height_g: height + options.gutter_y, // height + gutter
    center, // layout center
    count, // number of areas
    bush, // rtree index
    // layouting options
    options,
  }
}

// Draw manipulates a layout inplace
export let Draw = layout => {
  // so we dont taint the Layout object with functions
  // maybe thats to cautious

  let update = () => {
    // update other data structures
    let byId = dict(layout.areas.map((v, i) => [v.id, i]))
    layout.byId = byId
    layout.pages = layout.pages.filter(p => byId[p.id] || byId[p.id] === 0)
    layout.id = layoutId()
    merge(layout, index(layout.areas))
  }

  return {
    remove(ids) {
      layout.areas = layout.areas.filter(a => !ids.includes(a.id))
      update()
    },
    transformAreas(transformer) {
      layout.areas = layout.areas.map(a => {
        let transformed = transformer(a)
        // calculate derived center
        if (transformed != a) {
          let {x, y, width, height, page} = transformed
          transformed.center = {
            x: x + width / 2,
            y: y + height / 2,
          }
        }
        return transformed
      })
      update()
    },
  }
}

export let hashLayout = layout => ({
  renderHash: hashObj(layout.roptions),
  positionHash: hashObj({
    id: layout.id,
    o: layout.options,
    zo: layout.zoptions,
  }),
})
